import { useEffect } from "react";
import { CreateTenderFormGroups } from "@/constants/enums";
import { FormGroupItem } from "../Shared/types";

type DocumentCategory = {
  name: string;
  startIndex: number;
  length: number;
};

/**
 * Processes document files from form data and prepares them for submission
 * @param document - Record of document categories and their files
 * @param categoryFieldNames - Mapping of document categories to their form field names
 * @returns Object containing flattened documents array and category indexes
 *
 * @example
 * const { documents, categoryIndexes } = processDocuments(formData.document, {
 *   publication: "publications",
 *   serviceDescription: "serviceDescription",
 *   contract: "contracts"
 * });
 * // Add to variables
 * variables.documents = documents;
 * Object.entries(categoryIndexes).forEach(([key, value]) => {
 *   variables[key] = value;
 * });
 */
export function processDocuments(
  document: Record<string, File[]>,
  categoryFieldNames: Record<string, string>
) {
  // Extract and flatten all document files
  // Get an array of all the File[] arrays from the document object
  // e.g. if document = { publication: [file1, file2], contract: [file3] }
  // then extractedDocuments = [[file1, file2], [file3]]
  const extractedDocuments = Object.values(document);
  const documents = extractedDocuments.flat();

  // Calculate starting index for each category
  let currentIndex = 0;
  const categories: DocumentCategory[] = extractedDocuments.map((files, index) => {
    const category: DocumentCategory = {
      name: Object.keys(document)[index],
      startIndex: currentIndex,
      length: files.length
    };
    currentIndex += files.length;
    return category;
  });

  // Generate index arrays for each category
  const categoryIndexes = categories.reduce((acc, category) => {
    acc[categoryFieldNames[category.name]] = Array.from(
      { length: category.length },
      (_, i) => (i + category.startIndex).toString()
    );
    return acc;
  }, {} as Record<string, string[]>);

  return {
    documents,
    categoryIndexes
  };
}

/**
 * Determines the width of input fields based on the left panel width
 * @param leftPanelWidth - The width of the left panel
 * @param useInputDefault - Whether to use the default input width or not
 * @returns The width of the input field
 *
 * @example
 * const inputWidth = getInputWidth(leftPanelWidth);
 * // Add to className
 * <InputField className={inputWidth} />
 */
export function getInputWidth(leftPanelWidth: number, useInputDefault?: boolean): string {
  return leftPanelWidth < 52 ? "w-full" : (useInputDefault ? "" : "w-[48%]");
}

/**
 * Scrolls a container to a child element by id with an offset.
 * @param id - The id of the element to scroll to (e.g. "DESCRIPTION")
 * @param offset - The offset in pixels
 * @param containerId - The id of the scrollable container (e.g. "form-scroll-container")
 */
export function scrollToElementWithOffset(id: string, offset: number, containerId?: string) {
  // Add a small delay to ensure any layout updates have completed
  setTimeout(() => {
    const element = document.getElementById(id);
    const container = containerId ? document.getElementById(containerId) : null;

    if (element) {
      if (container) {
        // Get element's position relative to the container
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;

        // Calculate the position relative to the container's viewport
        const relativeTop = elementRect.top - containerRect.top;

        // Calculate the final scroll position
        const top = scrollTop + relativeTop - offset;

        // Scroll the container
        container.scrollTo({ top, behavior: "smooth" });
      } else {
        // Fallback: scroll window
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        window.scrollTo({ top: rect.top + scrollTop - offset, behavior: "smooth" });
      }
      // Optionally focus for accessibility
      element.focus?.();
    }
  }, 50); // 50ms delay
}

/**
 * Spy on the card position within the scroll container and set the active form group based on the closest card
 * to the top of the scroll container.
 * @param formGroups - Form groups that can be set to active
 * @param cardRefs - The refs of the form group cards
 * @param setActiveFormGroup - The function to set the active form group
 * @param offset - The offset to scroll to, from the top of the scroll container
 */
export function useFormGroupScrollSpy(
  formGroups: FormGroupItem[],
  cardRefs: React.RefObject<(HTMLDivElement | null)[]>,
  setActiveFormGroup: React.Dispatch<React.SetStateAction<CreateTenderFormGroups>>,
  offset = 0
) {
  useEffect(() => {
    const scrollContainer = document.getElementById("form-scroll-container");
    if (!scrollContainer) return;

    const handleScroll = () => {
      let closestIndex = 0;
      let closestDistance = Number.NEGATIVE_INFINITY;

      cardRefs.current?.forEach((ref, idx) => {
        if (ref) {
          // Get top relative to container, minus offset for sticky header
          const cardTop =
            ref.getBoundingClientRect().top -
            scrollContainer.getBoundingClientRect().top -
            offset;
          // We want the largest top that is <= 0 (i.e., just scrolled past or at the offset)
          if (cardTop <= 0 && cardTop > closestDistance) {
            closestDistance = cardTop;
            closestIndex = idx;
          }
        }
      });

      setActiveFormGroup(formGroups[closestIndex].group);
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    // Call once to set initial state
    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [formGroups, cardRefs, setActiveFormGroup, offset]);
}
